import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';

import { FormCreateAccountTypeData } from 'dashboard/forms/FormCreateAccountType/FormCreateAccountType';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { AccountType } from 'dashboard/models/account-type';
import { User } from 'dashboard/models/user';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import { loadAccountTypes } from 'dashboard/redux/thunk-actions/account-types';
import DashboardAccountTypesView from 'dashboard/views/DashboardAccountTypesView/DashboardAccountTypesView';
import { fieldSchemaErrorMessage, isFieldSchemaError } from 'lib/fields-errors';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import usePermission from 'lib/hooks/usePermission';
import { ApiModel } from 'lib/interfaces/api';
import { FeatureFlagsMap } from 'lib/interfaces/feature-flags';
import { Permission } from 'lib/permissions';
import {
  conflictErrorString,
  isConflictError,
  isPermissionsError,
  permissionsErrorMessage,
} from 'lib/permissions-errors';

interface DashboardAccountTypesRouteProps {
  currentUser: User;
  featureFlags: FeatureFlagsMap;
}

const DashboardAccountTypesRoute = (props: DashboardAccountTypesRouteProps & PropsFromRedux) => {
  const navigate = useNavigate();
  const { loading: isLoadingAccountTypes, callback: handleLoadAccountType } = useApiLoader({
    loader: () => props.loadAccountTypes(),
    deps: [props.currentEnvironment],
    skip: false,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load account types.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const hasAccountTypeViewPermission = usePermission(Permission.AccountTypeView);

  const { callback: createAccountType, error: createAccountTypeError } = useApiCallback(
    async (formData: FormCreateAccountTypeData) => {
      const accountType = await SessionClientDashboard.postResourceCreate<AccountType>({
        type: ApiModel.AccountType,
        attributes: {
          name: formData['name'],
        },
      });

      handleLoadAccountType();
      props.showMessage('Successfully created account type.', MessageType.Success, true);

      if (hasAccountTypeViewPermission) {
        navigate(`/dashboard/account-types/${accountType.data.id}`);
      }
    },
    [props.currentEnvironment, props.showMessage],
  );

  useApiErrorHandler({
    error: createAccountTypeError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else if (isConflictError(error)) {
        props.showMessage(conflictErrorString(error), MessageType.Failure);
      } else if (isFieldSchemaError(error)) {
        props.showMessage(fieldSchemaErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to create account type.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  return (
    <DashboardAccountTypesView
      accountTypes={props.accountTypes}
      createAccountType={createAccountType}
      isLoadingAccountTypes={isLoadingAccountTypes}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    accountTypes: state.accountTypes.accountTypes,
    environments: state.organization.environments,
    currentEnvironment: state.currentEnvironment.currentEnvironment,
  };
};

const mapDispatchToProps = {
  loadAccountTypes,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardAccountTypesRoute);
